import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Seo from "@/components/Seo";

export default function Articles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hero, setHero] = useState(null);

  useEffect(() => {
    api.get("/articles").then((r) => {
      setItems(r.data);
      setLoading(false);
    });
    // System page — admin-editable via the Pages tab. 404 is fine, we fall
    // back to the hard-coded headline below.
    api
      .get("/pages/_articles-hero")
      .then((r) => setHero(r.data))
      .catch(() => setHero(null));
  }, []);

  // Heuristic: highlight the last 2 words of the title in blue (matches the
  // original design). Falls back to whole-title styling if the title has < 2 words.
  const renderTitle = (raw) => {
    const text = (raw || "").trim();
    const words = text.split(/\s+/);
    if (words.length < 2) return <span>{text}</span>;
    const tail = words.slice(-2).join(" ");
    const head = words.slice(0, -2).join(" ");
    return (
      <>
        {head}
        {head && <br />}
        <span className="pcs-text-blue">{tail}</span>
      </>
    );
  };

  const heroTitle = hero?.title || "Field notes, guides & lists.";
  const heroKicker = hero?.meta_description || "The party blog";
  const heroImage = hero?.cover_image || "";

  return (
    <div data-testid="articles-page">
      <Seo
        title="Articles, guides & costume how-tos"
        description="Field notes, costume guides, and party-host playbooks. Pre-Halloween reading from the PartyCostumeShop editors."
        path="/articles"
      />
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className={heroImage ? "md:col-span-7" : "md:col-span-12"} data-testid="articles-hero-text">
            <div className="font-mono uppercase text-[11px] tracking-widest pcs-text-pink mb-3">
              {heroKicker}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
              {renderTitle(heroTitle)}
            </h1>
          </div>
          {heroImage && (
            <div className="md:col-span-5">
              <div className="brut bg-white overflow-hidden aspect-[4/3]">
                <img
                  src={heroImage}
                  alt={heroTitle}
                  data-testid="articles-hero-image"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="font-mono uppercase text-xs tracking-widest opacity-70">
            Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="brut bg-white p-8 text-center">
            <div className="font-display text-3xl mb-2">Drafts in progress.</div>
            <p className="text-gray-700">
              The original site had hundreds of these — we're rebuilding the
              best ones first.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
